import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Camera, X, ArrowLeft } from "lucide-react";
import { NavLink } from "react-router-dom";

export default function AddWish() {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [publicPagesEnabled, setPublicPagesEnabled] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/wedding")
      .then((r) => setPublicPagesEnabled(r.data.public_pages_enabled !== false))
      .catch(() => setPublicPagesEnabled(true))
      .finally(() => setLoadingConfig(false));
  }, []);

  const handleFiles = async (files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { id: r.data.id, preview: URL.createObjectURL(file) }]);
      } catch { toast.error("Foto uploaden mislukt"); }
    }
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!publicPagesEnabled) return;
    if (!name.trim() || !message.trim()) return toast.error("Vul je naam en boodschap in");
    setSubmitting(true);
    try {
      await api.post("/wishes", {
        friend_name: name, message, photo_ids: photos.map((p) => p.id),
      });
      setSent(true);
    } catch { toast.error("Versturen mislukt"); }
    finally { setSubmitting(false); }
  };

  if (sent) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="wish-sent">
        <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Grazie mille</p>
          <h1 className="font-script text-6xl text-[#006A71] mt-3">Dankjewel</h1>
          <div className="section-divider"><span className="section-divider-dot" /></div>
          <p className="font-serif italic text-lg text-[#4A6165] mt-4">
            Je boodschap is toegevoegd aan het album van Lien & Gust. Ze zullen het prachtig vinden.
          </p>
          <button onClick={() => { setSent(false); setName(""); setMessage(""); setPhotos([]); }} className="btn-primary mt-10">
            Nog een wens schrijven
          </button>
        </motion.div>
      </div>
    );
  }

  if (loadingConfig) {
    return <div className="p-10 text-center font-script text-3xl">Laden...</div>;
  }

  if (!publicPagesEnabled) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="wish-locked">
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Per gli amici</p>
        <h1 className="font-script text-6xl text-[#006A71] mt-3">Wensen tijdelijk gesloten</h1>
        <div className="section-divider"><span className="section-divider-dot" /></div>
        <p className="font-serif italic text-lg text-[#4A6165] mt-4">
          Nieuwe wensen kunnen momenteel alleen door een admin worden toegevoegd.
        </p>
        <NavLink to="/" className="btn-primary mt-10 inline-flex">Terug naar home</NavLink>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="add-wish-page">
      <NavLink to="/" className="text-[10px] tracking-[0.3em] uppercase text-[#4A6165] inline-flex items-center gap-2 hover:text-[#006A71]">
        <ArrowLeft className="w-3.5 h-3.5" /> Terug naar home
      </NavLink>
      <div className="text-center mt-8 mb-10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Per gli amici</p>
        <h1 className="font-script text-6xl text-[#006A71] mt-3">Schrijf je wens</h1>
        <div className="section-divider"><span className="section-divider-dot" /></div>
        <p className="font-serif italic text-[#4A6165] mt-4">
          Een lieve boodschap, een herinnering aan de bruiloft, of gewoon een dikke knuffel — alles is welkom.
        </p>
      </div>

      <motion.form
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="bg-white border border-[#006A71]/15 rounded-sm p-8 sm:p-12 brutal-shadow-lg"
        data-testid="wish-form"
      >
        <label className="block">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Jouw naam</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            className="scrap-input mt-1" placeholder="Bv. Marieke" data-testid="wish-name-input" />
        </label>
        <label className="block mt-8">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Je wens of herinnering</span>
          <textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)}
            className="scrap-input mt-1 resize-none" placeholder="Schrijf hier iets liefs..." data-testid="wish-message-input" />
        </label>
        <div className="mt-8">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Foto's (optioneel)</span>
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            {photos.map((p) => (
              <div key={p.id} className="relative w-20 h-20 border border-[#006A71]/30">
                <img src={p.preview} className="w-full h-full object-cover" alt="preview" />
                <button type="button" onClick={() => removePhoto(p.id)} className="absolute -top-2 -right-2 bg-[#FAF6EF] border border-[#006A71] text-[#006A71] rounded-full w-6 h-6 flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => fileRef.current?.click()}
              className="w-20 h-20 border border-dashed border-[#006A71]/40 flex items-center justify-center text-[#006A71] hover:bg-[#E8DFCF]/30 transition-colors"
              data-testid="wish-add-photo-btn">
              <Camera className="w-5 h-5" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => handleFiles(Array.from(e.target.files || []))} />
          </div>
        </div>
        <button type="submit" disabled={submitting} className="btn-primary mt-10 disabled:opacity-50" data-testid="wish-submit-btn">
          {submitting ? "Versturen..." : "Verstuur mijn wens"}
        </button>
      </motion.form>
    </div>
  );
}
